import { useState, type ReactNode } from 'react';
import { Plus, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUIStore } from '@/store/uiStore';
import { useTouchScrollDetection } from '@/hooks/useTouchScrollDetection';
import { cn } from '@/lib/utils';
import { toolbarControl } from '@/lib/buttonStyles';

export interface AddItemDropdownExtraItem {
  key: string;
  label: string;
  icon?: ReactNode;
  description?: string;
  onSelect: () => void;
  disabled?: boolean;
}

interface AddItemDropdownProps {
  /** @deprecated kept for back-compat with old callers; one unified look now. */
  variant?: 'icon' | 'compact' | 'full';
  className?: string;
  /** Extra menu items appended below the primary Add action (separated). */
  extraItems?: AddItemDropdownExtraItem[];
}

const primaryButtonClass = cn(
  'h-10 px-4 gap-2 shrink-0 transition-all duration-smooth border-0',
  'bg-active-gold/95 hover:bg-active-gold text-black font-semibold',
  'shadow-[0_1px_0_rgba(255,255,255,0.25)_inset,0_4px_12px_-4px_rgba(255,200,40,0.45)]',
);

/**
 * Single "+ Add" button → opens the unified ItemFormModal (fast row by default).
 * When `extraItems` are supplied, a secondary chevron renders next to the
 * primary button and opens a small menu of those extras (e.g. Bulk lot,
 * Collectr import, Select items). The primary button itself ALWAYS opens
 * Add Item — never a Quick/Full split.
 */
export function AddItemDropdown({ className, extraItems }: AddItemDropdownProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const touchHandlers = useTouchScrollDetection(setMenuOpen);
  const { openAddItemModal, openAddLotModal } = useUIStore();

  const hasExtras = !!extraItems && extraItems.length > 0;

  if (!hasExtras) {
    return (
      <Button
        type="button"
        onClick={openAddItemModal}
        className={cn(primaryButtonClass, toolbarControl, className)}
      >
        <Plus className="w-4 h-4" strokeWidth={2.5} />
        <span className="text-sm">Add</span>
      </Button>
    );
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          {...touchHandlers}
          type="button"
          className={cn(primaryButtonClass, toolbarControl, className)}
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          <span className="text-sm">Add</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 z-[60] overflow-hidden shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)_inset]"
      >
        <DropdownMenuItem
          onClick={() => {
            setMenuOpen(false);
            openAddItemModal();
          }}
          className="flex items-start gap-3 p-3 cursor-pointer"
        >
          <Plus className="w-4 h-4 mt-0.5 shrink-0 text-active-gold" strokeWidth={2.25} />
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">Add item</span>
            <span className="text-xs text-muted-foreground">Single card, sealed, or slab</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setMenuOpen(false);
            openAddLotModal();
          }}
          className="flex items-start gap-3 p-3 cursor-pointer"
        >
          <Layers className="w-4 h-4 mt-0.5 shrink-0 text-active-gold" strokeWidth={2.25} />
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">Add Lot</span>
            <span className="text-xs text-muted-foreground">
              One listing for a group of items
            </span>
          </div>
        </DropdownMenuItem>
        {extraItems!.map((item) => (
          <DropdownMenuItem
            key={item.key}
            disabled={item.disabled}
            onClick={() => {
              setMenuOpen(false);
              item.onSelect();
            }}
            className="flex items-start gap-3 p-3 cursor-pointer"
          >
            {item.icon && (
              <span className="mt-0.5 shrink-0 flex">{item.icon}</span>
            )}
            <div className="flex flex-col gap-0.5">
              <span className="font-medium">{item.label}</span>
              {item.description && (
                <span className="text-xs text-muted-foreground">
                  {item.description}
                </span>
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
